import {
  Box, CircularProgress, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ClipboardList, FileText, Printer, Download } from 'lucide-react';
import { AccordionSection } from '../common/AccordionSection';
import { EmptyState } from '../common/EmptyState';
import { Prescription } from '../../types';
import { formatDate } from '../../lib/utils';
import { handlePrescriptionPdf } from '../../lib/pdfUtils';
import { useTranslation } from 'react-i18next';

interface PatientPrescriptionsSectionProps {
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  canSeePrescriptions: boolean;
  prescriptions: Prescription[];
  loadingPrescriptions: boolean;
  setSelectedPrescription: (p: Prescription) => void;
  setShowPrescriptionView: (v: boolean) => void;
  processingPdfId: string | null;
  setProcessingPdfId: (id: string | null) => void;
}

export function PatientPrescriptionsSection({
  expanded,
  onExpandChange,
  canSeePrescriptions,
  prescriptions,
  loadingPrescriptions,
  setSelectedPrescription,
  setShowPrescriptionView,
  processingPdfId,
  setProcessingPdfId,
}: PatientPrescriptionsSectionProps) {
  const { t } = useTranslation('patients');
  const theme = useTheme();

  if (!canSeePrescriptions) return null;

  return (
    <AccordionSection
      panelId="prescriptions"
      expanded={expanded}
      onExpandChange={onExpandChange}
      icon={<ClipboardList size={20} color={theme.palette.success.main} />}
      title={t('previousPrescriptions')}
      count={prescriptions.length}
      detailsSx={{ p: 0 }}
    >
      {loadingPrescriptions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : prescriptions.length === 0 ? (
        <EmptyState message="No prescriptions found" icon={<ClipboardList size={40} />} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t('date')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('doctor')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('diagnosis')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('status')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prescriptions.map((rx) => (
                <TableRow key={rx.id} hover>
                  <TableCell>{formatDate(rx.createdAt)}</TableCell>
                  <TableCell>{rx.doctorName}</TableCell>
                  <TableCell>{rx.diagnosis || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={rx.status}
                      color={rx.status === 'DRAFT' ? 'warning' : rx.status === 'ISSUED' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton size="small" color="primary" title={t('view')}
                        onClick={() => { setSelectedPrescription(rx); setShowPrescriptionView(true); }}
                      >
                        <FileText size={16} />
                      </IconButton>
                      <PdfIconButton
                        rxId={rx.id}
                        processingPdfId={processingPdfId}
                        setProcessingPdfId={setProcessingPdfId}
                        rx={rx}
                        action="print"
                        icon={<Printer size={16} />}
                        title={t('print')}
                      />
                      <PdfIconButton
                        rxId={rx.id}
                        processingPdfId={processingPdfId}
                        setProcessingPdfId={setProcessingPdfId}
                        rx={rx}
                        action="download"
                        icon={<Download size={16} />}
                        title={t('download')}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AccordionSection>
  );
}

function PdfIconButton({
  rxId, processingPdfId, setProcessingPdfId, rx, action, icon, title,
}: {
  rxId: string;
  processingPdfId: string | null;
  setProcessingPdfId: (id: string | null) => void;
  rx: Prescription;
  action: 'print' | 'download';
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <IconButton
      size="small"
      disabled={processingPdfId === rxId}
      title={title}
      onClick={async () => {
        setProcessingPdfId(rxId);
        await handlePrescriptionPdf({
          prescriptionId: rxId,
          action,
          patientName: rx.patientName,
          prescriptionDate: rx.status === 'ISSUED' ? (rx.issuedAt || rx.createdAt) : rx.createdAt,
        });
        setProcessingPdfId(null);
      }}
    >
      {icon}
    </IconButton>
  );
}
